import { useMutation, useQuery, useQueryClient } from 'react-query';
import { request, requestXlsxFileDownload, request_v2 } from '../utils/axios-utils';
import { approveRequestFromUI, rejectRequestFromUI } from '../apis/requestApi';

const GetReshootRequest = async (pageNum, pageSize, skuName, stage, clientCode, category, type, approvalStatus, from, to) => {
    let parentParams = {
        pageNum,
        pageSize,
        skuName,
        stage,
        clientCode,
        category,
        type,
        // approvalStatus,
    };

    if (!!from && !!to) {
        parentParams['requestApprovedAt'] = JSON.stringify({ from, to });
    } else {
        delete parentParams['requestApprovedAt'];
    }
    const { data } = await request({
        method: 'get',
        url: '/requests/listRequests',
        params: {
            ...parentParams,
        },
    });
    return data;
};
export const useGetReshootRequest = (pageNum, pageSize, skuName, stage, clientCode, category, type, approvalStatus, from, to) =>
    useQuery(
        ['ReshootRequest', pageNum, pageSize, skuName, stage, clientCode, category, type, approvalStatus, from, to],
        () => GetReshootRequest(pageNum, pageSize, skuName, stage, clientCode, category, type, approvalStatus, from, to),
        {
            refetchOnWindowFocus: false,
        }
    );

const DownloadReshootRequestExcel = async data => {
    let { from, to } = data;
    let parentParams = {
        ...data,
    };

    if (!!from && !!to) {
        delete parentParams.from;
        delete parentParams.to;
        parentParams['requestApprovedAt'] = JSON.stringify({ from, to });
    } else {
        delete parentParams['requestApprovedAt'];
    }
    return await requestXlsxFileDownload({
        method: 'get',
        url: '/requests/generateExcel',
        params: {
            ...parentParams,
        },
    });
};

export const useDownloadExcelReshootRequest = onSuccess => {
    return useMutation(DownloadReshootRequestExcel, {
        onSuccess: onSuccess,
        // onError: onError,
        // refetchOnWindowFocus: false,
    });
};

const fetchCategoryList = () => {
    return request({
        url: '/requests/listReshootCategories',
        method: 'GET',
    });
};

export const useCategoryListFetch = () => {
    return useQuery(['listCategory'], fetchCategoryList, {
        refetchOnWindowFocus: false,
    });
};
const fetchTypeList = () => {
    return request({
        url: '/requests/getTypes',
        method: 'GET',
    });
};

export const useFetchTypeList = () => {
    return useQuery(['listType'], fetchTypeList, {
        refetchOnWindowFocus: false,
    });
};

export const useApproveRequest = (onSuccess, onError) => {
    const queryClient = useQueryClient();

    // Get current user info for optimistic update
    const getCurrentUser = () => {
        try {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        } catch {
            return null;
        }
    };

    return useMutation(
        ({ requestId, isShootable }) => approveRequestFromUI(requestId, isShootable),
        {
            onMutate: async ({ requestId }) => {
                // Cancel any outgoing refetches to avoid overwriting optimistic update
                await queryClient.cancelQueries('ReshootRequest');

                // Snapshot the previous value for all query variations
                const previousQueries = queryClient.getQueriesData('ReshootRequest');

                const user = getCurrentUser();
                const now = new Date();

                // Optimistically update all matching queries (handles different filter combinations)
                queryClient.setQueriesData('ReshootRequest', oldData => {
                    if (!oldData?.data) return oldData;

                    const updatedData = oldData.data.map(item => {
                        if (item.requestId === requestId) {
                            return {
                                ...item,
                                approvalStatus: true,
                                actionBy: user?.name || 'Current User',
                                actionType: 'Approved',
                                requestApprovedAt: now.toISOString(),
                                requestProcessedStatus: 'Pending',
                            };
                        }
                        return item;
                    });

                    // Only return updated data if we found the item to update
                    const foundItem = oldData.data.find(item => item.requestId === requestId);
                    if (foundItem) {
                        return {
                            ...oldData,
                            data: updatedData,
                        };
                    }

                    return oldData;
                });

                // Return context with snapshot for rollback
                return { previousQueries };
            },
            onSuccess: (data, variables, context) => {
                // Refetch in background to ensure data consistency with server
                // This will update the optimistic data with server response
                queryClient.invalidateQueries('ReshootRequest', { refetchType: 'active' });
                if (onSuccess) onSuccess(data);
            },
            onError: (error, variables, context) => {
                // Rollback optimistic update on error
                if (context?.previousQueries) {
                    context.previousQueries.forEach(([queryKey, queryData]) => {
                        queryClient.setQueryData(queryKey, queryData);
                    });
                }
                if (onError) onError(error);
            },
        });
};

export const useRejectRequest = (onSuccess, onError) => {
    const queryClient = useQueryClient();

    // Get current user info for optimistic update
    const getCurrentUser = () => {
        try {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        } catch {
            return null;
        }
    };

    return useMutation(
        ({ requestId, rejectionReason }) => rejectRequestFromUI(requestId, rejectionReason),
        {
            onMutate: async ({ requestId }) => {
                // Cancel any outgoing refetches to avoid overwriting optimistic update
                await queryClient.cancelQueries('ReshootRequest');

                // Snapshot the previous value for all query variations
                const previousQueries = queryClient.getQueriesData('ReshootRequest');

                const user = getCurrentUser();
                const now = new Date();

                // Optimistically update all matching queries (handles different filter combinations)
                queryClient.setQueriesData('ReshootRequest', oldData => {
                    if (!oldData?.data) return oldData;

                    const updatedData = oldData.data.map(item => {
                        if (item.requestId === requestId) {
                            return {
                                ...item,
                                approvalStatus: false,
                                actionBy: user?.name || 'Current User',
                                actionType: 'Rejected',
                                requestApprovedAt: now.toISOString(),
                                requestProcessedStatus: 'Rejected',
                            };
                        }
                        return item;
                    });

                    // Only return updated data if we found the item to update
                    const foundItem = oldData.data.find(item => item.requestId === requestId);
                    if (foundItem) {
                        return {
                            ...oldData,
                            data: updatedData,
                        };
                    }

                    return oldData;
                });

                // Return context with snapshot for rollback
                return { previousQueries };
            },
            onSuccess: (data, variables, context) => {
                // Refetch in background to ensure data consistency with server
                // This will update the optimistic data with server response
                queryClient.invalidateQueries('ReshootRequest', { refetchType: 'active' });
                if (onSuccess) onSuccess(data);
            },
            onError: (error, variables, context) => {
                // Rollback optimistic update on error
                if (context?.previousQueries) {
                    context.previousQueries.forEach(([queryKey, queryData]) => {
                        queryClient.setQueryData(queryKey, queryData);
                    });
                }
                if (onError) onError(error);
            },
        });
};
